import {Fragment, React} from '@api';
import {useStores} from '@modules'
import {SpecPdfImage} from '@modules/pdf'
import {Link, Text} from '@react-pdf/renderer';
import {nav} from '@stores'
import {ProductDataWorker} from '@webWorkers';
import {styled} from '@pdf'

interface MyProps {
  data: Array<{ id?: string }>
}

const SpecValues = styled.View`
  display:flex;
  flex-direction:column;
`

const HorizontalLine = styled.View`
  opacity: 0.2;
  background-color: #bec3ce;
  width:100%;
  height:2px;
  margin:1mm 0 0 0;
`

const HorizontalDivider = styled.View`
  opacity: 0.25;
  background-color: #bec3ce;
  width:2px;
  height:10mm;
  margin:3mm 4mm;  
`

const SpecValueRow = styled.View`
  display:flex;
  flex-direction:row;
  padding: 4mm 0;
  margin: auto 0;
  flex-grow: 0;
  flex-shrink:0;
`

const SpecValueRowCol1 = styled.View`
  display:flex;
  flex-direction:row;  
  width:20%;
  flex-shrink:0;
`

const Col1SpecPdfImage = styled.View`
width: 20mm;
height:20mm;
margin: 0 auto;
flex-shrink:0;
flex-grow: 0;
`

const Col1SpecTextColumn = styled.View`
flex-grow: 1;
flex-shrink:1;
display:flex;
flex-direction: column;
padding: 2mm 8mm;
`

const Col1SpecTitle = styled.Text`
text-transform: uppercase;
font-size:15px;
letter-spacing: 2px;
margin-bottom:2px;
`

const Col1SpecSubtitle = styled.Text`
font-size:10px;
`

const SpecValueRowCol2 = styled.View`
  flex-grow:1;
  width:80%;
  flex-wrap: wrap;
  display:flex;
  flex-direction:row;
  padding-left:2cm;  
`

const SwatchesForSpec = styled.View`
  display:flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex-grow: 1;
  flex-shrink: 0;
  padding: 0 0 1mm 0;
`

const SpecSwatch = styled.View`
  display:flex;
  flex-direction: column;
  font-family:Arial;
  text-transform: none;
  min-width:20mm;
  max-width:40mm;
  flex-grow:1;
  flex-shrink: 1; 
  //border:solid 1px black;
  padding-bottom:1mm;
`

const SpecSwatchImage = styled.View`
min-width: 20mm;
height: 20mm;
margin: 0 auto;
padding: 0 0 2mm 0;

//border-bottom-style: solid;
//border-bottom-width: 2px;
//border-bottom-color: black;
`

const SpecSwatchTitle = styled.Text`
margin:0 auto;
padding: 1mm 2mm;
font-size:10px;
text-overflow: ellipsis;
flex-shrink:1;
max-lines: 2;
`

const SpecSwatchSubtitle = styled.Text`
margin:0 auto;
font-size:8px;
flex-shrink:1;
text-overflow: ellipsis;
max-lines: 2;
`

const SpecSwatchProductCount = styled.Text`
display:flex;
color: #9E9E9E;
font-size:8px;
margin:auto;
padding:0 1mm;
text-overflow: ellipsis;
`

export const GroupByGlossaryPdfPages: React.FunctionComponent<MyProps> = ({data}) => {
  const {
        catalog: {catalogConfig: {fields}, libraryTypes, cv: {cardViewWithAttributes}},
        nav: {urlForObject},
        pdf
    }= useStores(),
    {renderPage, config: {imageSize}} = pdf

  let specs = fields.filter(v => !v.def)

  let products = data.map(p => ProductDataWorker.convertToSynthetic(p, cardViewWithAttributes, null, imageSize))
  return <Fragment>
    {_.isEmpty(specs) ?
     renderPage({children: <Text>No Results</Text>})
     : specs.map((spec, i) => {
      const lt = libraryTypes[spec.field]
      const groupedValues = Object.entries(_.groupBy(products, p => p[spec.field] ? p[spec.field].id : null))
      .map(([specValueId, products]) => ({specValue: products[0][spec.field], products}))
      .filter(({specValue}) => specValue && !specValue.blank)
      return !_.isEmpty(groupedValues) &&
        _.chunk(groupedValues, 6)
        .map((groupedValues, j) =>
          renderPage({
            key:             j,
            summaryTitle:    `${groupedValues.length} ${groupedValues.length == 1 ? lt.def.displayName.singular : lt.def.displayName.plural}`,
            summarySubtitle: `${products.length} Product${products.length > 1 ? 's' : ''}`,
            pageTitle:       spec.label,
            children: (
                        <SpecValues>
                          {groupedValues.map(({products, specValue, specValue: {title, subtitle, images, imagesRaw, exId}}) => {
                            //console.log({specValue, products})

                            // Get every other spec that isn't this one and find unique values for each spec across the products assigned to THIS spec
                            // This is very similar to the swatch determination logic
                            let otherSpecs: any = specs
                            .filter(s => s != spec)
                            .map(spec => ({
                              spec,
                              grouped: _.groupBy(products, p => {
                                const v = p[spec.field]
                                if (!v || v.blank) {
                                  return ''
                                } else {
                                  return v.id
                                }
                              })
                            })).map(({spec, grouped}) => ({
                              spec,
                              products,
                              entries: Object.entries(grouped)
                                      .map(([id, products]) => ({specValue: products && products[0][spec.field], products}))
                                      .filter(({specValue}) => specValue && !specValue.blank)
                            }))

                            const flattened = _.flatMap(otherSpecs.map(({spec, entries, products}) => entries.map(e => ({...e, spec, products}))))
                            let type        = specValue.type ? specValue.type.name : specValue.__typename

                            return <Fragment key={specValue.id}>
                              <HorizontalLine/>
                              <SpecValueRow>
                                <SpecValueRowCol1>
                                  <Col1SpecPdfImage>
                                    <Link src={urlForObject(specValue)}>
                                      <SpecPdfImage spec={specValue}/>
                                    </Link>
                                  </Col1SpecPdfImage>
                                  <Col1SpecTextColumn>
                                    <Link src={urlForObject(specValue)}>
                                      <Col1SpecTitle>{title || '--'}</Col1SpecTitle>
                                    </Link>
                                    <Link src={urlForObject(specValue)}>
                                      <Col1SpecSubtitle>{subtitle || '--'}</Col1SpecSubtitle>
                                    </Link>
                                  </Col1SpecTextColumn>
                                </SpecValueRowCol1>
                                <SpecValueRowCol2>
                                  <SwatchesForSpec>
                                    {flattened.map(({specValue, specValue: {id, exId, images, title, subtitle, __typename}, products}, i) =>
                                      <Fragment key={id}>
                                        {i > 0 && <HorizontalDivider/>}
                                        <Link src={nav.urlForObject(specValue)}>
                                          <SpecSwatch>
                                            <SpecSwatchImage>
                                              <SpecPdfImage spec={specValue}/>
                                            </SpecSwatchImage>
                                            <SpecSwatchTitle>
                                              {title || '--'}
                                            </SpecSwatchTitle>
                                            <SpecSwatchSubtitle>{subtitle || '--'}</SpecSwatchSubtitle>
                                            {false && <SpecSwatchProductCount>({products.length})</SpecSwatchProductCount>}
                                          </SpecSwatch>
                                        </Link>

                                      </Fragment>
                                    )}
                                  </SwatchesForSpec>
                                </SpecValueRowCol2>
                              </SpecValueRow>
                            </Fragment>
                          })}
                        </SpecValues>
                      )
          }))
    })}
  </Fragment>
}



// WEBPACK FOOTER //
// ./src/modules/pdf/groupBy/GroupByGlossaryPdfPages.tsx